import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'
import { FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms'

import { FlxStyleModule, FlxThemeModule } from '@flowx/angular-theme'
import { FlxIconModule, FlxSwitchModule } from '@flowx/angular-ui-toolkit'
import { LabelAccessibilityProps, SwitchTemplateConfig } from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxMarkdownPipe } from '../../pipes/flx-markdown.pipe'
import { getDynamicPropSignal, getErrorMessages, getThemeIcon } from '../helpers'

@Component({
  selector: 'flx-switch-wrapper',
  imports: [
    CommonModule,
    FlxSwitchModule,
    FlxLocalizePipe,
    ReactiveFormsModule,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
    FlxMarkdownPipe,
  ],
  templateUrl: './switch-wrapper.component.html',
  styleUrl: './switch-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxSwitchWrapperComponent implements OnInit {
  #injector = inject(Injector)
  templateConfig = input.required<SwitchTemplateConfig>()
  control = input.required<FormControl>()
  errors = input<ValidationErrors | null>(null)
  showErrors = input<boolean>(false)
  disabledState = input<boolean>(false)

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)

  icons = computed(() => ({
    selected: getThemeIcon(ThemeComponent.Checkbox, 'selectedIcon'),
    unselected: getThemeIcon(ThemeComponent.Checkbox, 'unselectedIcon'),
    helper: getThemeIcon(ThemeComponent.HelperTooltip, 'helperIcon'),
  }))

  readonly elementUniqueId = `${crypto.randomUUID()}`
  readonly labelId = `${this.elementUniqueId}-form-item-label`
  readonly helperId = `${this.elementUniqueId}-helper`
  readonly errorId = `${this.elementUniqueId}-error`

  readonly describedByIds = computed(() => {
    const ids: string[] = []
    const helper = this.helper()
    const showErrors = this.showErrors()
    const errors = this.errors()

    if (showErrors && errors) {
      Object.keys(errors).forEach((_, index) => {
        ids.push(`${this.errorId}${index}`)
      })
    } else if (helper) {
      ids.push(this.helperId)
    }
    return ids.join(' ')
  })

  // dynamic props
  label: Signal<string>
  helper: Signal<string>
  errorMessages: Record<string, Signal<string>>
  ariaLabel: Signal<string>

  ngOnInit(): void {
    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    runInInjectionContext(this.#injector, () => {
      const config = this.templateConfig()
      const { label, helper } = this.flowxProps()
      this.label = getDynamicPropSignal(label, this.templateConfig())
      this.helper = getDynamicPropSignal(helper, this.templateConfig())
      this.errorMessages = getErrorMessages(config)
      this.ariaLabel = computed(() => this.accessibility()?.label)
    })
  }
}
